import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { MatButtonModule } from "@angular/material/button";
import { Subscription } from "rxjs";
import { BscIndicadorNorma, BscNorma, BscRes, CorteBsc } from "../../shared/model/bsc/bsc.model";
import { BscService } from "../bsc.service";
import { guardarDescarga, mensajeDeDescarga } from "../bsc-descarga";
import { fechaHora, meta, pct } from "../bsc-formato";
import { SEMAFORO } from "../bsc-estados";
import { SelectorCorteBscComponent, SeleccionCorteBsc } from "../selector-corte/selector-corte-bsc.component";
import { AvanceIndicadorBscComponent } from "./avance-indicador-bsc.component";

@Component({
  selector: "app-indicadores-generales-bsc",
  standalone: true,
  imports: [CommonModule, RouterLink, MatButtonModule, SelectorCorteBscComponent, AvanceIndicadorBscComponent],
  templateUrl: "./indicadores-generales-bsc.component.html",
  styleUrls: ["../../dashboard/shared/dashboard-tarjetas.css", "../bsc.shared.css", "./indicadores-generales-bsc.component.css"],
})
export class IndicadoresGeneralesBscComponent implements OnInit, OnDestroy {
  anioInicial: number | null = null;
  corteInicial: CorteBsc | null = null;
  anio: number | null = null;
  corte: CorteBsc = "ANUAL";
  idNorma?: number;
  normas: BscNorma[] = [];
  filas: ReturnType<typeof filaIndicador>[] = [];
  definidaPor = "";
  definidaEl = "";
  loading = false;
  exportando = false;
  error = "";
  errorExportar = "";
  private consulta?: Subscription;
  private descarga?: Subscription;
  private destruido = false;

  constructor(private readonly bsc: BscService, private readonly route: ActivatedRoute,
    private readonly router: Router, private readonly cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    const anio = Number(params.get("anio"));
    this.anioInicial = Number.isInteger(anio) && anio >= 2000 && anio <= 2100 ? anio : null;
    const corte = (params.get("corte") ?? "").toUpperCase() as CorteBsc;
    this.corteInicial = ["1", "2", "3", "4", "ANUAL"].includes(corte) ? corte : null;
    const norma = Number(params.get("idNorma"));
    this.idNorma = Number.isInteger(norma) && norma > 0 ? norma : undefined;
  }

  ngOnDestroy(): void {
    this.destruido = true;
    this.consulta?.unsubscribe();
    this.descarga?.unsubscribe();
  }

  onSeleccion(s: SeleccionCorteBsc): void {
    this.anio = s.anio;
    this.corte = s.corte ?? "ANUAL";
    this.consultar();
  }

  elegirNorma(id?: number): void {
    this.idNorma = id;
    this.consultar();
  }

  get total(): number {
    return this.normas.reduce((suma, n) => suma + (n.total ?? 0), 0);
  }

  consultar(): void {
    if (this.anio == null) { return; }
    this.router.navigate([], { relativeTo: this.route,
      queryParams: { anio: this.anio, corte: this.corte, idNorma: this.idNorma }, replaceUrl: true });
    this.consulta?.unsubscribe();
    this.loading = true;
    this.error = "";
    this.errorExportar = "";
    this.filas = [];
    this.consulta = this.bsc.indicadoresConsultar(this.anio, this.corte, this.idNorma).subscribe({
      next: (res) => this.aplicar(res),
      error: () => {
        this.loading = false;
        this.error = "No se pudieron consultar los indicadores. Revisa tu conexión e intenta de nuevo.";
        this.cdr.markForCheck();
      },
    });
  }

  private aplicar(res: BscRes): void {
    this.loading = false;
    if (res.codigoError) {
      this.error = res.mensajeError ?? "No se pudieron consultar los indicadores.";
    } else {
      this.normas = res.normas ?? [];
      this.filas = (res.indicadores ?? []).map((i) => filaIndicador(i, this.normas));
      this.definidaPor = res.definidaPor ?? "Sin registro de definición";
      this.definidaEl = fechaHora(res.definidaEl);
    }
    this.cdr.markForCheck();
  }

  exportar(): void {
    if (this.anio == null || this.idNorma == null || this.exportando || this.loading) { return; }
    this.exportando = true;
    this.errorExportar = "";
    const nombre = `Indicadores-${this.anio}-${this.idNorma}.xlsx`;
    this.descarga = this.bsc.indicadoresExportar(this.anio, this.idNorma, this.corte).subscribe({
      next: (res) => {
        guardarDescarga(res, nombre);
        this.exportando = false;
        this.cdr.markForCheck();
      },
      error: async (err: unknown) => {
        const mensaje = await mensajeDeDescarga(err, "exportar la evidencia");
        if (this.destruido) { return; }
        this.errorExportar = mensaje;
        this.exportando = false;
        this.cdr.markForCheck();
      },
    });
  }
}

function filaIndicador(i: BscIndicadorNorma, normas: BscNorma[]) {
  const real = [i.acumuladoQ1 ?? null, i.acumuladoQ2 ?? null, i.acumuladoQ3 ?? null, i.acumuladoQ4 ?? null];
  const valores = [i.valorQ1, i.valorQ2, i.valorQ3, i.valorQ4];
  return {
    id: i.idNormaKpi,
    objetivo: i.objetivo ?? "Sin objetivo",
    indicador: i.indicador ?? "",
    area: i.area ?? "—",
    norma: normas.find((n) => n.idNorma === i.idNorma)?.nombre ?? "—",
    meta: meta(i.metaValor, i.metaTexto, i.unidad),
    trimestres: valores.map((v, q) => v == null ? pct(real[q]) : meta(v, null, i.unidad)),
    real,
    alcance: pct(i.alcanceCorte),
    ancho: Math.min(100, Math.max(0, (i.alcanceCorte ?? 0) * 100)),
    estado: SEMAFORO.find((s) => s.valor === i.semaforo),
  };
}
